/* eslint-disable no-param-reassign */
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { Handle, NodeProps, Position } from 'reactflow';
import { X, GripVertical } from 'lucide-react';
import { ResponseCardNodeData } from './ResponseCardNode';
import ResponseCardsTreeNodeItem from '../../../../types/BotBuilder/ResponseCardsTreeNodeItem';

interface SortableResponseCardProps {
  card: ResponseCardsTreeNodeItem;
  id: string;
  index: number;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => void;
  handleKeyDown: (
    e: React.KeyboardEvent,
    card: ResponseCardsTreeNodeItem,
    index: number
  ) => void;
  responseStepRefs: React.MutableRefObject<(HTMLTextAreaElement | null)[]>;
  nodeProps: NodeProps<ResponseCardNodeData>;
  handleDelete: (index: number) => void;
  canDelete: boolean;
  isHighlighted?: boolean;
  isLocked?: boolean;
}

function SortableResponseCard({
  card,
  id,
  index,
  handleChange,
  handleKeyDown,
  responseStepRefs,
  nodeProps,
  handleDelete,
  canDelete,
  isHighlighted = false,
  isLocked = false
}: Readonly<SortableResponseCardProps>): React.ReactElement | null {
  const { isConnectable } = nodeProps;

  const {
    attributes,
    listeners,
    setNodeRef,
    transition,
    transform,
    isDragging
  } = useSortable({
    id,
    animateLayoutChanges: () => false
  });

  const containerStyle = {
    ...(transform && {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`
    }),
    ...(transition && { transition })
  };

  return (
    <div
      className={`nodrag relative ${isDragging ? 'z-10' : 'z-[1]'}`}
      style={containerStyle}
      ref={setNodeRef}
    >
      <div className='relative w-80 group'>
        <div
          className={`
          relative flex items-stretch rounded-md overflow-hidden transition-all duration-200
          ${isDragging ? 'bg-[#049A8F] shadow-lg' : 'bg-[#012438] hover:bg-[#334f5f]'}
          ${isHighlighted ? 'ring-2 ring-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]' : ''}
          focus-within:ring-2 focus-within:ring-[#057D99] focus-within:ring-opacity-50
        `}
        >
          <div
            className='drag-handle cursor-grab flex items-center justify-center px-2 py-3
                       border-r border-white/10 transition-all duration-200 hover:bg-white/5'
            {...attributes}
            {...listeners}
          >
            <GripVertical
              size={14}
              color='#fff'
              className='opacity-70 group-hover:opacity-100'
            />
          </div>

          <div className='flex-1 relative'>
            <textarea
              ref={(el: HTMLTextAreaElement | null): void => {
                responseStepRefs.current[index] = el;
              }}
              value={card.text}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => {
                handleChange(e, index);

                const { target } = e;
                target.style.height = 'auto';
                target.style.height = `${Math.max(40, target.scrollHeight)}px`;
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>): void =>
                handleKeyDown(e, card, index)
              }
              maxLength={255}
              rows={1}
              className='w-full min-h-[40px] pt-2.5 pb-2 px-3 text-white text-base bg-transparent
                         border-none rounded-none resize-none placeholder-white/40 leading-normal
                         focus:outline-none focus:ring-0 focus:placeholder-white/60 overflow-hidden'
              placeholder=''
              disabled={isLocked}
            />
          </div>

          {canDelete && !isLocked && (
            <div
              className='flex items-center justify-center px-2 py-3
                         border-l border-white/10 cursor-pointer transition-all duration-200
                         hover:bg-red-500/20 hover:text-red-300 group/delete'
              onClick={(): void => handleDelete(index)}
              onKeyDown={(e): void => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDelete(index);
                }
              }}
              role='button'
              tabIndex={0}
              aria-label='delete'
            >
              <X
                size={14}
                color='#fff'
                className='opacity-70 group-hover/delete:opacity-100 transition-opacity'
              />
            </div>
          )}
        </div>
      </div>

      <Handle
        type='source'
        position={Position.Right}
        id={id}
        style={{
          position: 'absolute',
          right: -26,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1
        }}
        isConnectable={isConnectable}
      />
    </div>
  );
}

export default SortableResponseCard;
